import { GetPlattformMessagesParams, MessagePrimaryChannelStatus, MessageType, ValidationStatus } from "@bvhi/backend-api";
import { Button, BVHISelect, Card, CardContent, CardHeader, Collapse, Grid, TextField } from "@bvhi/ui-base";
import { ClearIcon } from "@bvhi/ui-icons";
import isEqual from "lodash/isEqual";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIExpandComponent } from "../../../../../components/bvhi-expand-component";
import { defaultMessagesFilter } from "./default-messages-filter";

interface Props {
  filter: GetPlattformMessagesParams;
  onChange: ({ key, value }: { key: string; value: string | string[] }) => void;
  onReset: () => void;
}

export const MessagesFilterCard = ({ filter, onChange, onReset }: Props) => {
  const { t } = useTranslation(["enums", "messages", "common", "userstatus"]);
  const [expanded, setExpanded] = useState<boolean>(false);

  const isResetDisabled = useMemo(() => {
    return isEqual(filter, defaultMessagesFilter);
  }, [filter]);

  return (
    <Card>
      <CardHeader
        restrictContentWidth={false}
        title={t("messages:messages.card.filter.title")}
        action={
          <Button color="secondary" disabled={isResetDisabled} endIcon={<ClearIcon />} onClick={onReset} variant="text">
            {t("common:button.reset.filter")}
          </Button>
        }
      />
      <CardContent>
        <Grid container pb={2} spacing={2}>
          <Grid xs={12}>
            <TextField
              fullWidth
              label={t("messages:messages.card.filter.searchtext.label")}
              placeholder={t("messages:messages.card.filter.searchtext.placeholder")}
              value={filter.searchString ?? ""}
              onChange={(e) => {
                onChange({ key: "searchString", value: e.target.value });
              }}
            />
          </Grid>
        </Grid>
        <BVHIExpandComponent expanded={expanded} setExpanded={setExpanded} />
        <Collapse in={expanded} timeout="auto">
          <Grid container spacing={2}>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("messages:datagrid.supplierCode")}
                placeholder={t("messages:messages.filter.supplierCode.placeholder")}
                value={filter.supplierCode ?? ""}
                onChange={(e) => {
                  onChange({ key: "supplierCode", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("messages:datagrid.customerNumber")}
                placeholder={t("messages:messages.filter.customerNumber.placeholder")}
                value={filter.customerNumber ?? ""}
                onChange={(e) => {
                  onChange({ key: "customerNumber", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("messages:datagrid.orderNumber")}
                placeholder={t("messages:datagrid.orderNumber")}
                value={filter.orderNumber ?? ""}
                onChange={(e) => {
                  onChange({ key: "orderNumber", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("messages:datagrid.purchaseOrderReference")}
                placeholder={t("messages:messages.filter.purchaseOrderReference.placeholder")}
                value={filter.purchaseOrderReference ?? ""}
                onChange={(e) => {
                  onChange({ key: "purchaseOrderReference", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <BVHISelect
                displayEmpty
                fullWidth
                label={t("messages:datagrid.messageType")}
                multiple
                showReset
                value={filter.messageType ?? []}
                handleChange={(value) => {
                  onChange({ key: "messageType", value: value });
                }}
                handleReset={() => {
                  onChange({ key: "messageType", value: [] });
                }}
                items={[
                  {
                    key: MessageType.DESPATCH_ADVICE,
                    value: MessageType.DESPATCH_ADVICE,
                    label: t("enums:enums.message.type.despatch.advice"),
                  },
                  { key: MessageType.INVOICE, value: MessageType.INVOICE, label: t("enums:enums.message.type.invoice") },
                  { key: MessageType.ORDER, value: MessageType.ORDER, label: t("enums:enums.message.type.order") },
                  { key: MessageType.OTHER, value: MessageType.OTHER, label: t("enums:enums.message.type.other") },
                  { key: MessageType.TEST, value: MessageType.TEST, label: t("enums:enums.message.type.test") },
                  {
                    key: MessageType.TRANSPORT_ACKNOWLEDGEMENT,
                    value: MessageType.TRANSPORT_ACKNOWLEDGEMENT,
                    label: t("enums:enums.message.type.transport.acknowledgement"),
                  },
                ]}
              />
            </Grid>
            <Grid xs={6}>
              <BVHISelect
                displayEmpty
                fullWidth
                label={t("messages:datagrid.messagePrimaryChannelStatus")}
                multiple
                showReset
                value={filter.messagePrimaryChannelStatus ?? []}
                handleChange={(value) => {
                  onChange({ key: "messagePrimaryChannelStatus", value: value });
                }}
                handleReset={() => {
                  onChange({ key: "messagePrimaryChannelStatus", value: [] });
                }}
                items={[
                  {
                    key: MessagePrimaryChannelStatus.ACTIVE,
                    value: MessagePrimaryChannelStatus.ACTIVE,
                    label: t("enums:enums.message.primary.channel.status.active"),
                  },
                  {
                    key: MessagePrimaryChannelStatus.DELIVERED,
                    value: MessagePrimaryChannelStatus.DELIVERED,
                    label: t("enums:enums.message.primary.channel.status.delivered"),
                  },
                  {
                    key: MessagePrimaryChannelStatus.FAILED,
                    value: MessagePrimaryChannelStatus.FAILED,
                    label: t("enums:enums.message.primary.channel.status.failed"),
                  },
                  {
                    key: MessagePrimaryChannelStatus.STALLED,
                    value: MessagePrimaryChannelStatus.STALLED,
                    label: t("enums:enums.message.primary.channel.status.stalled"),
                  },
                ]}
              />
            </Grid>
            <Grid xs={6}>
              <BVHISelect
                displayEmpty
                fullWidth
                label={t("messages:datagrid.messageValidationStatus")}
                multiple
                showReset
                value={filter.messageValidationStatus ?? []}
                handleChange={(value) => {
                  onChange({ key: "messageValidationStatus", value: value });
                }}
                handleReset={() => {
                  onChange({ key: "messageValidationStatus", value: [] });
                }}
                items={[
                  {
                    key: ValidationStatus.INVALID,
                    value: ValidationStatus.INVALID,
                    label: t("enums:enums.validation.status.invalid"),
                  },
                  {
                    key: ValidationStatus.OPEN,
                    value: ValidationStatus.OPEN,
                    label: t("enums:enums.validation.status.open"),
                  },
                  {
                    key: ValidationStatus.VALID,
                    value: ValidationStatus.VALID,
                    label: t("enums:enums.validation.status.valid"),
                  },
                ]}
              />
            </Grid>
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
};
